import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { 
  Search, 
  TrendingUp, 
  Zap, 
  Users, 
  Clock,
  ArrowRight,
  Activity
} from 'lucide-react';
import Web3 from 'web3';
import TransactionDetails from '../components/TransactionDetails';
import WalletDetails from '../components/WalletDetails';
import BlockchainMonitor from '../utils/BlockchainMonitor';
import Footer from '../components/Footer.js';
import LOGO from '../assets/logo180.png';
import { Link } from "react-router-dom";


const Container = styled.div`
background: linear-gradient(135deg, #020611 0%, #050B15 50%, #020611 100%);  min-height: 100vh;
  color: #ffffff;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
`;

const Header = styled.header`
  padding: 1rem 2rem;
  background: rgba(11, 20, 38, 0.95);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  position: sticky;
  top: 0;
  z-index: 100;
`;

const Nav = styled.nav`
  display: flex;
  align-items: center;
  justify-content: space-between;
  max-width: 1200px;
  margin: 0 auto;
`;

const Logo = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 1.5rem;
  font-weight: 700;
  text-decoration: none;
  color: white;
`;

const LogoIcon = styled.img`
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #3B82F6 0%, #1E40AF 100%);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  
`;

const NavLinks = styled.div`
  display: flex;
  gap: 2rem;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const NavLink = styled(Link)`
  color: #94A3B8;
  text-decoration: none;
  font-weight: 500;
  transition: color 0.3s ease;
  
  &:hover {
    color: #3B82F6;
  }
`;

const PublishButton = styled.button`
  background: #3B82F6;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.3s ease;
  
  &:hover {
    background: #2563EB;
  }

    @media screen and (max-width: 926px) {
    display: none;
    }
`;

const SearchIcon = styled(Search)`
  color: #94A3B8;
  width: 20px;
  height: 20px;
`;

const HeroSection = styled.section`
  text-align: center;
  padding: 6rem 2rem 4rem 2rem;
  max-width: 1200px;
  margin: 0 auto;
  
`;



const HeroTitle = styled.h1`
  font-size: 4rem;
  font-weight: 800;
  margin: 0 0 1rem 0;
  line-height: 1.1;

  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const HeroHighlight = styled.span`
  background: linear-gradient(135deg, #3B82F6 0%, #06B6D4 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const HeroSubtitle = styled.p`
  font-size: 1.25rem;
  color: #94A3B8;
  margin: 0 0 3rem 0;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

const SearchContainer = styled.div`
  display: flex;
  max-width: 600px;
  margin: 0 auto;
  background: rgba(30, 41, 59, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  overflow: hidden;
  position: relaive;
  box-shadow: 0 0 20px rgba(6, 182, 212, 0.3), 0 0 40px rgba(6, 182, 212, 0.1);
`;

const SearchInput = styled.input`
  flex: 1;
  padding: 1rem 1rem 1rem 3rem;
  background: transparent;
  border: none;
  color: #ffffff;
  font-size: 1rem;
  outline: none;
  
  &::placeholder {
    color: #64748B;
  }
  @media screen and (max-width: 952px) {
  font-size: 12px;
  padding: 0.5rem;
  }
`;

const SearchButton = styled.button`
  background: #3B82F6;
  color: white;
  border: none;
  padding: 1rem 2rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.3s ease;
  
  &:hover {
    background: #2563EB;
  }
    
`;

const SearchIconContainer = styled.div`
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
`;

const SearchWrapper = styled.div`
  position: relative;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
  max-width: 1200px;
  margin: 4rem auto;
  padding: 0 2rem;
`;

const StatCard = styled.div`
  background: rgba(255, 255, 255, 0.025);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 2rem;
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-4px);
    border-color: rgba(59, 130, 246, 0.5);
    box-shadow: 0 20px 40px rgba(59, 130, 246, 0.1);
  }
`;

const StatHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
`;

const StatIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: ${props => props.color};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.iconColor};
`;

const StatChange = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  color: ${props => props.positive ? '#10B981' : '#EF4444'};
  font-size: 0.875rem;
  font-weight: 600;
`;

const StatTitle = styled.div`
  color: #94A3B8;
  font-size: 0.875rem;
  font-weight: 500;
  margin-bottom: 0.5rem;
`;

const StatValue = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: #ffffff;
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  max-width: 1200px;
  margin: 2rem auto;
  padding: 0 2rem;
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    margin-bottom: 0;
  }
`;

const Section = styled.div`
  background: rgba(255, 255, 255, 0.025);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 2rem;
  backdrop-filter: blur(10px);
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 2rem;
`;

const SectionIcon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: ${props => props.color};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.iconColor};
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: #ffffff;
  margin: 0;
`;

const BlockItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  
  &:last-child {
    border-bottom: none;
  }
`;

const BlockInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const BlockNumber = styled.div`
  color: #3B82F6;
  font-weight: 600;
  font-size: 1.125rem;
`;

const BlockDetail = styled.div`
  color: #94A3B8;
  font-size: 0.875rem;
`;

const BlockStats = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.25rem;
`;

const TransactionItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  
  &:last-child {
    border-bottom: none;
  }
`;

const TxHash = styled.div`
  color: #3B82F6;
  font-weight: 600;
`;

const TxStatus = styled.div`
  background: rgba(16, 185, 129, 0.2);
  color: #10B981;
  padding: 0.25rem 0.75rem;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
`;

const TxAddresses = styled.div`
  color: #94A3B8;
  font-size: 0.875rem;
  margin: 0.25rem 0;
`;

const TxValue = styled.div`
  color: #ffffff;
  font-weight: 600;
  text-align: right;
`;

const TxTime = styled.div`
  color: #64748B;
  font-size: 0.75rem;
`;

const Home = () => {
  const [networkStats, setNetworkStats] = useState({
    latestBlock: 0,
    gasPrice: '0',
    chainId: 0,
    isConnected: false,
    totalSupply: '10,000,000,000',
    activeValidators: 4,
    marketCap: 0.00,
    transactions: 0,
    validators: 524891
  });


  const [recentBlocks, setRecentBlocks] = useState([]);
  const [showTransactionDetails, setShowTransactionDetails] = useState(false);
  const [showWalletDetails, setShowWalletDetails] = useState(false);
  const [currentSearchQuery, setCurrentSearchQuery] = useState('');
  const [currentWalletAddress, setCurrentWalletAddress] = useState('');
  const [recentTransactions, setRecentTransactions] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [transactionData, setTransactionData] = useState(null);
  const [activeCount, setActiveCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const web3 = new Web3('https://chainfree.info:5010/validator/3');

  // 'https://217.154.13.58:8545'

  const fetchValidatorCount = async () => {
    setLoading(true);
    await BlockchainMonitor.checkValidatorHealth();
    const summary = BlockchainMonitor.getHealthSummary();
    setActiveCount(summary.healthyValidators);
    setLoading(false);
  };

  useEffect(() => {
    fetchValidatorCount();

   
  }, []);

  useEffect(() => {
  fetchNetworkStats();
  fetchRecentBlocks();
  fetchRecentTransactions();
  updateTransactionCount(); // Call this instead
  
  const interval = setInterval(() => {
    fetchNetworkStats();
    fetchRecentBlocks();
    fetchRecentTransactions();
    updateTransactionCount(); // Update incrementally every 5 seconds
  }, 5000);

  return () => clearInterval(interval);
}, []);

  const fetchNetworkStats = async () => {
    try {
      const latestBlock = await web3.eth.getBlockNumber();
      const gasPrice = await web3.eth.getGasPrice();
      const chainId = await web3.eth.getChainId();
      
      setNetworkStats(prev => ({
        ...prev,
        latestBlock,
        gasPrice: Web3.utils.fromWei(gasPrice, 'gwei'),
        chainId,
        isConnected: true
      }));
    } catch (error) {
      console.error('Error fetching network stats:', error);
      setNetworkStats(prev => ({ ...prev, isConnected: false }));
    }
  };

  const fetchRecentBlocks = async () => {
  try {
    const latestBlockNumber = await web3.eth.getBlockNumber();
    const latestBlockNum = Number(latestBlockNumber);
    const blocks = [];
    
    for (let i = 0; i < 5 && latestBlockNum - i >= 0; i++) {
      const block = await web3.eth.getBlock(latestBlockNum - i);
      const gasPrice = await web3.eth.getGasPrice(); // Get current gas price
      
      blocks.push({
        ...block,
        gasPrice: Web3.utils.fromWei(gasPrice, 'gwei') // Convert to Gwei
      });
    }
    
    setRecentBlocks(blocks);
  } catch (error) {
    console.error('Error fetching recent blocks:', error);
  }
};

const fetchRecentTransactions = async () => {
  try {
    const latestBlockNumber = await web3.eth.getBlockNumber();
    const latestBlockNum = Number(latestBlockNumber);
    console.log('Latest block number:', latestBlockNum);
    
    const transactions = [];
    
    // Look through the last 100 blocks to find transactions (covers ~3+ minutes)
    for (let i = 0; i < 100 && latestBlockNum - i >= 0 && transactions.length < 10; i++) {
      const block = await web3.eth.getBlock(latestBlockNum - i, true);
      
      if (block && block.transactions && block.transactions.length > 0) {
        console.log(`Found ${block.transactions.length} transactions in block ${latestBlockNum - i}`);
        
        for (const tx of block.transactions) {
          if (transactions.length < 10) {
            transactions.push({
              ...tx,
              timestamp: Number(block.timestamp),
              blockNumber: Number(block.number)
            });
          }
        }
      }
    }
    
    // Sort by block number (newest first)
    transactions.sort((a, b) => b.blockNumber - a.blockNumber);
    
    console.log('Found total transactions:', transactions.length);
    setRecentTransactions(transactions.slice(0, 5)); // Show only latest 5
  } catch (error) {
    console.error('Error fetching recent transactions:', error);
  }
};

const updateTransactionCount = async () => {
  try {
    const lastProcessedBlock = parseInt(localStorage.getItem('lastProcessedBlock') || '0');
    const latestBlock = Number(await web3.eth.getBlockNumber());
    
    let totalCount = parseInt(localStorage.getItem('totalTxCount') || '0');
    
    // Only process new blocks since last check
    for (let i = lastProcessedBlock + 1; i <= latestBlock; i++) {
      const block = await web3.eth.getBlock(i, false);
      if (block && block.transactions) {
        totalCount += block.transactions.length;
      }
    }
    
    localStorage.setItem('totalTxCount', totalCount.toString());
    localStorage.setItem('lastProcessedBlock', latestBlock.toString());
    
    // Format for display
   let displayValue;
        if (totalCount >= 1000000) {
        displayValue = (totalCount / 1000000).toFixed(1) + 'M';
        } else if (totalCount >= 1000) {
        displayValue = (totalCount / 1000).toFixed(1) + 'K';
        } else {
        displayValue = totalCount.toString();
        }

        setNetworkStats(prev => ({
        ...prev,
        transactions: displayValue  // Update the existing transactions field
        }));
            return totalCount;
        } catch (error) {
            console.error('Error updating transaction count:', error);
        }
        };

  // Helper function to validate Ethereum address
  const isValidAddress = (address) => {
    return /^0x[a-fA-F0-9]{40}$/.test(address);
  };

  // Helper function to validate transaction hash
  const isValidTxHash = (hash) => {
    return /^0x[a-fA-F0-9]{64}$/.test(hash);
  };
      
   const handleSearch = async () => {
  console.log('handleSearch called with:', searchQuery);
  
  if (!searchQuery.trim()) return;

  const cleanQuery = searchQuery.trim();

  try {
    // Check if it's a wallet address (42 characters including 0x)
    if (isValidAddress(cleanQuery)) {
      console.log('Valid wallet address detected');
      setCurrentWalletAddress(cleanQuery);
      setShowWalletDetails(true);
      setShowTransactionDetails(false);
      return;
    }

    // Check if it's a transaction hash (66 characters including 0x)
    if (isValidTxHash(cleanQuery)) {
      console.log('Valid transaction hash detected');
      
      const tx = await web3.eth.getTransaction(cleanQuery);
      const receipt = await web3.eth.getTransactionReceipt(cleanQuery);
      const block = await web3.eth.getBlock(tx.blockNumber);

      const transactionData = {
        ...tx,
        receipt,
        block,
        timestamp: block.timestamp
      };

      setCurrentSearchQuery(cleanQuery);
      setTransactionData(transactionData);
      setShowTransactionDetails(true);
      setShowWalletDetails(false);
      return;
    }

    // If it starts with 0x but doesn't match either format
    if (cleanQuery.startsWith('0x')) {
      alert('Invalid format. Please enter a valid wallet address (42 chars) or transaction hash (66 chars)');
      return;
    }

    // If it doesn't start with 0x
    alert('Please enter a valid wallet address or transaction hash starting with 0x');
    
  } catch (err) {
    console.error('Search error:', err);
    alert('Not found. Please check the address or transaction hash and try again.');
  }
};

function downloadHydrixWallet() {
  // Create a temporary link element
  const link = document.createElement('a');
  link.href = 'https://chainfree.info:5010/wallet-download/hydrix-wallet-beta.zip';
  link.download = 'hydrix-wallet-beta.zip';
  
  // Trigger the download
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  
  // Optional: Show a toast/notification
  console.log('✅ Hydrix Wallet download started');
  
  // Optional: Track download analytics
  // gtag('event', 'download', { item: 'hydrix-wallet-beta' });
}

  return (
    <>
       {showWalletDetails ? (
         <WalletDetails 
           address={currentWalletAddress}
           onBack={() => setShowWalletDetails(false)}
         />
       ) : showTransactionDetails ? (
          <TransactionDetails 
            transactionData={transactionData}
            searchQuery={currentSearchQuery}
            onBack={() => setShowTransactionDetails(false)}
          />
       ) : (
    <Container>
      <Header>
        <Nav>
          <Logo to="/">
            <LogoIcon src={LOGO} />
            Hydrixscan
          </Logo>
          <NavLinks>
            <NavLink href="#">Blockchain</NavLink>
            <NavLink href="#">Tokens</NavLink>
            <NavLink href="#">NFTs</NavLink>
            <NavLink href="#">Resources</NavLink>
            <NavLink to="/testnetfaucet">Faucet</NavLink>
          </NavLinks>
          <PublishButton onClick={downloadHydrixWallet}>Download Chrome Wallet</PublishButton>
          <SearchIcon />
        </Nav>
      </Header>

      <HeroSection>
        
        <HeroTitle>
          Explore the <HeroHighlight>Blockchain</HeroHighlight>
        </HeroTitle>
        <HeroSubtitle>
          Search transactions, addresses, blocks, and tokens on the blockchain
        </HeroSubtitle>
        
        <SearchWrapper>
          <SearchContainer>
            <SearchInput
              type="text"
              placeholder="Search by Address / Txn Hash / Block / Token"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            />
            <SearchButton onClick={handleSearch}>Search</SearchButton>
          </SearchContainer>
        </SearchWrapper>
      </HeroSection>

      <StatsGrid>
        <StatCard>
          <StatHeader>
            <StatIcon color="rgba(59, 130, 246, 0.2)" iconColor="#3B82F6">
              <TrendingUp size={24} />
            </StatIcon>
            <StatChange positive>+0.00%</StatChange>
          </StatHeader>
          <StatTitle>Market Cap</StatTitle>
          <StatValue>${networkStats.marketCap}B</StatValue>
        </StatCard>

        <StatCard>
          <StatHeader>
            <StatIcon color="rgba(6, 182, 212, 0.2)" iconColor="#06B6D4">
              <Zap size={24} />
            </StatIcon>
            <StatChange positive>+0.00%</StatChange>
          </StatHeader>
          <StatTitle>Transactions</StatTitle>
          <StatValue>{networkStats.transactions}</StatValue>
        </StatCard>

        <StatCard>
          <StatHeader>
            <StatIcon color="rgba(6, 182, 212, 0.2)" iconColor="#06B6D4">
              <Users size={24} />
            </StatIcon>
            <StatChange positive>+0.00%</StatChange>
          </StatHeader>
          <StatTitle>Validators</StatTitle>
          <StatValue>{loading ? "Loading..." : activeCount}</StatValue>
        </StatCard>

        <StatCard>
          <StatHeader>
            <StatIcon color="rgba(59, 130, 246, 0.2)" iconColor="#3B82F6">
              <Activity size={24} />
            </StatIcon>
            <StatChange negative>-0.00%</StatChange>
          </StatHeader>
          <StatTitle>Gas Price</StatTitle>
          <StatValue>{parseFloat(networkStats.gasPrice).toFixed(0)} Hydron</StatValue>
        </StatCard>
      </StatsGrid>

      <ContentGrid>
        <Section>
          <SectionHeader>
            <SectionIcon color="rgba(6, 182, 212, 0.2)" iconColor="#06B6D4">
              <Clock size={18} />
            </SectionIcon>
            <SectionTitle>Latest Blocks</SectionTitle>
          </SectionHeader>
          
          {recentBlocks.map((block) => (
            <BlockItem key={Number(block.number)}>
                <BlockInfo>
                <BlockNumber>{Number(block.number).toLocaleString()}</BlockNumber>
                <BlockDetail>{Math.floor((Date.now() - Number(block.timestamp) * 1000) / 1000)} secs ago</BlockDetail>
                <BlockDetail>Validator: {block.miner.slice(0, 6)}...{block.miner.slice(-4)}</BlockDetail>
                </BlockInfo>
                <BlockStats>
                    <BlockDetail>{block.transactions.length} txns</BlockDetail>
                    <BlockDetail>Gas: {parseFloat(networkStats.gasPrice).toFixed(1)} Hydron</BlockDetail>                
                </BlockStats>
            </BlockItem>
            ))}
        </Section>

        <Section>
  <SectionHeader>
    <SectionIcon color="rgba(6, 182, 212, 0.2)" iconColor="#06B6D4">
      <ArrowRight size={18} />
    </SectionIcon>
    <SectionTitle>Latest Transactions</SectionTitle>
  </SectionHeader>
  
  {recentTransactions.length > 0 ? (
    recentTransactions.map((tx, index) => (
      <TransactionItem key={tx.hash}>
        <div>
          <TxHash>{tx.hash.slice(0, 6)}...{tx.hash.slice(-4)}</TxHash>
          <TxStatus>success</TxStatus>
          <TxAddresses>
            {tx.from.slice(0, 6)}...{tx.from.slice(-4)} → {tx.to ? `${tx.to.slice(0, 6)}...${tx.to.slice(-4)}` : 'Contract Creation'}
          </TxAddresses>
          <TxTime>{Math.floor((Date.now() - tx.timestamp * 1000) / 1000)} secs ago</TxTime>
        </div>
        <div>
          <TxValue>{Web3.utils.fromWei(tx.value, 'ether')} HYX</TxValue>
        </div>
      </TransactionItem>
    ))
  ) : (
    <div style={{ color: '#64748B', textAlign: 'center', padding: '2rem' }}>
      No transactions found in recent blocks
    </div>
  )}
</Section>
      </ContentGrid>
      <Footer />
    </Container>
       )};
  </>
  );
};

export default Home;